import { ChatWithDataMessageRole } from '@recurrency/core-api-schema/dist/chatWithData/chatWithData';

import { getGlobalAppState } from 'hooks/useGlobalApp';

import { captureError } from 'utils/error';

import { ContentBlockType, ToolBlockStatus } from './types';
import type { ChatMessage, ConversationSummary, StoredConversation } from './types';

/**
 * Client-side persistence for chat-with-data conversations, scoped per tenant + user.
 *
 * This module is the single seam between the chat hook and storage. Keys are
 * namespaced by the active tenant id and user id (`rai:Chat.<tenantId>.<userId>.*`) so
 * switching tenants — or a different user signing in on a shared browser — shows a
 * different set of conversations. It holds two stores under that namespace: the
 * full-page chat's multi-conversation history (an index of summaries plus one key per
 * conversation, so a debounced save rewrites only the active one and the sidebar reads
 * only metadata) and the page-aware drawer's single rolling conversation (one key,
 * capped to the most recent messages). It's per-browser localStorage — when a backend
 * conversation API exists, these bodies swap out and the hook keeps calling the same
 * functions. Tenant/user switches reload the page, so reading them at key-build time is
 * enough (no in-memory reactivity needed).
 *
 * No migration of pre-existing data — each tenant+user scope starts fresh.
 */

// Active tenant + user namespace. Falls back to placeholders pre-init (never hit on the
// chat page, which is gated behind a tenant and an authenticated user).
function scope(): string {
  const { activeTenant, activeUser } = getGlobalAppState();
  return `${activeTenant?.id ?? 'unknown'}.${activeUser?.id ?? 'unknown'}`;
}

const indexKey = () => `rai:Chat.${scope()}.Index`;
const activeIdKey = () => `rai:Chat.${scope()}.ActiveId`;
const conversationKey = (id: string) => `rai:Chat.${scope()}.Conversation.${id}`;
const rollingKey = (name: string) => `rai:Chat.${scope()}.Rolling.${name}`;

// Cap stored conversations per tenant to protect the ~5MB localStorage budget
// (tables/charts can be large); oldest beyond this are pruned on save.
export const MAX_STORED_CONVERSATIONS = 25;

// Cap messages persisted per conversation. A long session's real weight is the
// table/chart artifacts, not the message count — this is a coarse safety valve so
// one conversation can't grow unbounded. Only the most recent are kept on disk.
const MAX_MESSAGES_PER_CONVERSATION = 100;

function readJson<T>(key: string): T | null {
  try {
    const json = localStorage.getItem(key);
    return json ? (JSON.parse(json) as T) : null;
  } catch (err) {
    // Corrupt/unparseable payload — drop it rather than crash the page.
    captureError(err);
    localStorage.removeItem(key);
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    // e.g. QuotaExceededError when a conversation's tables/charts are large.
    captureError(err);
  }
}

function readIndex(): ConversationSummary[] {
  return readJson<ConversationSummary[]>(indexKey()) ?? [];
}

function summarize(conv: StoredConversation): ConversationSummary {
  return {
    id: conv.id,
    title: conv.title,
    createdAt: conv.createdAt,
    updatedAt: conv.updatedAt,
    sessionId: conv.sessionId,
  };
}

// Most-recently-active first. updatedAt only changes when a message is sent (loads,
// selects, renames don't bump it), so the list reorders only on real activity.
function byUpdatedDesc(index: ConversationSummary[]): ConversationSummary[] {
  return [...index].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
}

export function listConversations(): ConversationSummary[] {
  return byUpdatedDesc(readIndex());
}

const INTERRUPTED_TEXT = 'The previous response was interrupted. Please try again.';

/**
 * Rehydrate stored messages: restore Date timestamps, complete stale running
 * tool blocks, and mark incomplete assistant messages with an interrupted notice.
 */
function rehydrateMessages(raw: ChatMessage[]): ChatMessage[] {
  return raw.map((msg) => {
    const timestamp = new Date(msg.timestamp);
    if (msg.role === ChatWithDataMessageRole.User) return { ...msg, timestamp };

    const blocks = (msg.blocks ?? []).map((block) =>
      block.type === ContentBlockType.Tool && block.status === ToolBlockStatus.Running
        ? { ...block, status: ToolBlockStatus.Done }
        : block,
    );

    // Incomplete assistant message — append interrupted notice.
    if (!msg.done) {
      blocks.push({ type: ContentBlockType.Text, text: INTERRUPTED_TEXT });
    }

    return { ...msg, timestamp, activeToolName: undefined, done: true, blocks };
  });
}

export function loadConversation(id: string): StoredConversation | null {
  const conv = readJson<StoredConversation>(conversationKey(id));
  if (!conv) return null;
  try {
    return { ...conv, messages: rehydrateMessages(conv.messages) };
  } catch {
    // Stored format is incompatible
    return {
      ...conv,
      messages: [
        {
          id: 'load-error',
          role: ChatWithDataMessageRole.Assistant,
          timestamp: new Date(),
          blocks: [
            { type: ContentBlockType.Text, text: 'This conversation could not be loaded. Please start a new one.' },
          ],
        },
      ],
    };
  }
}

export function saveConversation(conversation: StoredConversation): void {
  // Persist only the most recent messages so a long session can't blow the quota.
  // (title/timestamps are unaffected, so the summary stays correct.)
  const toStore =
    conversation.messages.length > MAX_MESSAGES_PER_CONVERSATION
      ? { ...conversation, messages: conversation.messages.slice(-MAX_MESSAGES_PER_CONVERSATION) }
      : conversation;

  writeJson(conversationKey(toStore.id), toStore);

  // Upsert the summary and keep newest-first.
  let index = byUpdatedDesc([...readIndex().filter((s) => s.id !== toStore.id), summarize(toStore)]);

  // Enforce the cap: prune oldest beyond the limit, removing their conversation keys.
  if (index.length > MAX_STORED_CONVERSATIONS) {
    index.slice(MAX_STORED_CONVERSATIONS).forEach((s) => localStorage.removeItem(conversationKey(s.id)));
    index = index.slice(0, MAX_STORED_CONVERSATIONS);
  }
  writeJson(indexKey(), index);
}

export function deleteConversation(id: string): void {
  localStorage.removeItem(conversationKey(id));
  writeJson(
    indexKey(),
    readIndex().filter((s) => s.id !== id),
  );
  if (getActiveId() === id) localStorage.removeItem(activeIdKey());
}

export function getActiveId(): string | null {
  try {
    return localStorage.getItem(activeIdKey());
  } catch {
    return null;
  }
}

export function setActiveId(id: string): void {
  try {
    localStorage.setItem(activeIdKey(), id);
  } catch (err) {
    captureError(err);
  }
}

// --- Rolling single-conversation store (page-aware drawer) ---
// The drawer has no conversation sidebar, so its whole state is one key. `maxMessages`
// (passed by the hook) caps how much is retained, so it can't grow unbounded.

export interface RollingHistory {
  /** Backend session id, replayed to continue server-side context. */
  sessionId?: string;
  messages: ChatMessage[];
}

export function loadRollingHistory(name: string): RollingHistory | null {
  const stored = readJson<RollingHistory>(rollingKey(name));
  if (!stored) return null;
  return {
    sessionId: stored.sessionId,
    messages: rehydrateMessages(stored.messages ?? []),
  };
}

export function saveRollingHistory(name: string, history: RollingHistory, maxMessages: number): void {
  // Keep only the most recent messages. Each send appends exactly [user, assistant], so
  // slicing an even count off the end preserves whole exchanges.
  writeJson(rollingKey(name), { sessionId: history.sessionId, messages: history.messages.slice(-maxMessages) });
}

export function clearRollingHistory(name: string): void {
  try {
    localStorage.removeItem(rollingKey(name));
  } catch (err) {
    captureError(err);
  }
}
